<template>
	<div class="register" v-loading="initLoading">
		<div class="form">
			<el-form :model="login_form" ref="ruleFormRef">
				<el-form-item
					prop="shopName"
					:rules="[
						{
							required: true,
							validator: validatorName,
						},
					]"
				>
					<el-input
						v-model="login_form.shopName"
						:placeholder="$t('login.pl_shop_name')"
						autocomplete="off"
					>
						<template #append v-if="!state.platForm.is_random_domain_prefix">
							.{{ state.platForm.domain }}
						</template>
					</el-input>
				</el-form-item>
				<el-form-item
					prop="category_id"
					:rules="[{ validator: validCategory }]"
					v-if="plainfo.category_status"
				>
					<el-select
						v-model="login_form.category_id"
						style="width: 100%"
						:placeholder="`${$t('login.pl_category')}${
							!plainfo.category_require ? `(${$t('login.optional')})` : ''
						}`"
					>
						<el-option
							v-for="item in categorys"
							:key="item.id"
							:label="item.name"
							:value="item.id"
						/>
					</el-select>
				</el-form-item>
				<el-form-item
					prop="mobile"
					:rules="[
						{ required: true, message: $t('login.pl_phone'), whitespace: true, trigger: 'blur' },
						{ validator: validatePass, trigger: 'blur' },
					]"
				>
					<el-input
						v-model="login_form.mobile"
						:placeholder="$t('login.pl_phone')"
						autocomplete="off"
					>
						<template #prepend>
							<AreaCode v-model="login_form.areaCode" />
						</template>
					</el-input>
				</el-form-item>
				<el-form-item
					v-if="needCode || !invite_code"
					prop="code"
					:rules="[
						{ required: true, message: $t('login.pl_code'), whitespace: true, trigger: 'change' },
					]"
				>
					<el-row :gutter="10">
						<el-col :span="12">
							<el-input
								v-model="login_form.code"
								:placeholder="$t('login.pl_code')"
								autocomplete="off"
							/>
						</el-col>
						<el-col :span="12">
							<Verification
								:formData="{
									sms: 'registerCode',
									are: login_form.areaCode,
									mobile: login_form.mobile,
								}"
							/>
						</el-col>
					</el-row>
				</el-form-item>
				<el-form-item
					prop="password"
					:rules="[
						{ required: true, message: $t('login.not_empty'), whitespace: true, trigger: 'change' },
						{ validator: validatePass1, trigger: 'change' },
					]"
				>
					<el-input
						v-model="login_form.password"
						:type="showPass ? 'text' : 'password'"
						:placeholder="$t('login.pl_password')"
						autocomplete="off"
					>
						<template #suffix>
							<i
								class="shopyy-iconfont shopyyicon-Invisible"
								style="cursor: pointer"
								:class="
									showPass
										? 'shopyy-iconfont shopyyicon-View'
										: 'shopyy-iconfont shopyyicon-Invisible'
								"
								@click="showPass = !showPass"
							>
							</i>
						</template>
					</el-input>
				</el-form-item>
				<el-form-item
					prop="againPassword"
					:rules="[
						{ required: true, message: $t('login.not_empty'), whitespace: true, trigger: 'change' },
						{ validator: validatePass1, trigger: 'change' },
					]"
				>
					<el-input
						v-model="login_form.againPassword"
						:type="againpass ? 'text' : 'password'"
						:placeholder="$t('login.pl_pass_again')"
						autocomplete="off"
					>
						<template #suffix>
							<i
								class="shopyy-iconfont shopyyicon-Invisible"
								style="cursor: pointer"
								:class="
									againpass
										? 'shopyy-iconfont shopyyicon-View'
										: 'shopyy-iconfont shopyyicon-Invisible'
								"
								@click="againpass = !againpass"
							>
							</i>
						</template>
					</el-input>
				</el-form-item>
				<el-form-item
					v-if="plainfo.is_ka == 1 && plainfo.default_pid_mode === 2"
					prop="pid"
					:rules="[{ required: true, message: $t('login.pl_pid'), trigger: 'change' }]"
				>
					<el-select style="width: 100%" v-model="login_form.pid" :placeholder="$t('login.pl_pid')">
						<el-option value="" :label="$t('login.pl_pid')"></el-option>
						<el-option
							v-for="item in plainfo.default_pusers"
							:key="item.id"
							:value="item.id"
							:label="item.mobile_number"
						/>
					</el-select>
				</el-form-item>
				<el-form-item v-if="plainfo.menu_coupon_status">
					<el-input
						v-model="login_form.coupon"
						:placeholder="$t('login.pl_coupon')"
						autocomplete="off"
					/>
				</el-form-item>
				<el-form-item>
					<div class="clause">
						<el-checkbox v-model="checked" size="large">{{ $t('login.i_agree') }}</el-checkbox>
						<router-link to="/terms" target="_blank">{{ $t('login.terms_policies') }}</router-link>
					</div>
				</el-form-item>
			</el-form>
			<div class="form-btn">
				<el-button type="primary" :disabled="!checked" :loading="loading" @click="save">
					{{ $t('login.reg') }}
				</el-button>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { Ref, inject, ref, watch, computed } from 'vue'
import AreaCode from './compoent/AreaCode.vue'
import type { Register } from '../types/login'
import { getCategorys, getLimit, register } from '../api/login'
import Verification from './compoent/Verification.vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { useStore } from '/@/store'
import { Cookies } from '/@/utils/storage'
import { getNowDomain } from '/@/utils/config'
import { i18n } from '/@/i18n'
import { useRoute } from 'vue-router'

const route = useRoute()
const needCode = ref(false)
const invite_code = route.query.params ? JSON.parse(route.query.params as any)?.invite_code : ''

const { t } = i18n.global
const { state } = useStore()
const login_form = ref<Register>({
	shopName: '',
	areaCode: '',
	mobile: Cookies.get('phone') || '',
	code: '',
	password: '',
	againPassword: '',
	coupon: '',
	utm_medium: Cookies.get('utm_medium') || '',
	utm_source: Cookies.get('utm_source') || '',
	ref: Cookies.get('ref') || '',
	ref_channel: Cookies.get('ref_channel') || '',
	brand_id: state.platForm.id,
	category_id: '',
	pid: '',
})

const ruleFormRef = ref()
const showPass = ref(false)
const againpass = ref(false)
const checked = ref(false)
const loading = ref(false)

//验证手机号
const validatePass = (rule: any, value: any, callback: any) => {
	if (value.length < 5 || value.length > 18) {
		callback(t('login.valid_phone'))
	} else {
		callback()
	}
}
const brandIds = [120, 197, 209]
const showCode = computed(() => {
	return brandIds.includes(state.platForm.id)
})
//验证密码长度
const validatePass1 = (origin: any, value: any, cb: any) => {
	let field = 'againPassword'
	if (origin.field === 'againPassword') {
		field = 'password'
	}

	if (!showCode.value) {
		const regex = /^(?=.*[A-Za-z])(?=.*\d).{7,}$/
		if (!regex.test(value)) {
			return cb(t('login.valid_pass_rule'))
		}
	}
	// if (login_form.value[field] && value) {
	// 	if (login_form.value[field] !== value) cb(t('layout.pass_not'))
	// 	else ruleFormRef.value.clearValidate(field)
	// }

	if (
		origin.field === 'againPassword' &&
		login_form.value.password !== login_form.value.againPassword
	) {
		cb(t('layout.pass_not'))
	}
	if (showCode.value) {
		const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/ //华为密码验证正则
		if (value === login_form.value.mobile) {
			cb(t('layout.hw_password_v2'))
		}
		if (!regex.test(value)) {
			cb(t('layout.hw_password_v'))
		}
		if (value && value.length < 8) {
			return cb(t('layout.hw_password_v3'))
		}
	}
	cb()
}

const validatorName = (_: any, val: any, cb: any) => {
	const newVal = val.trim()
	if (!newVal) return cb(t('login.pl_shop_name'))
	const names: string[] = domainLimit.value.disable_name.split(',')
	const isInName = names.some((item) => newVal.includes(item))
	if (isInName) return cb(domainLimit.value.disable_desc)
	cb()
}

const save = () => {
	ruleFormRef.value?.validate(async (valid) => {
		if (!valid) return
		const names: string[] = domainLimit.value.warning_name.split(',')
		const isInName = names.some((item) => login_form.value.shopName.includes(item))
		if (isInName) {
			ElMessageBox({
				title: t('common.warning'),
				message: domainLimit.value.warning_desc,
				showCancelButton: true,
				confirmButtonText: t('common.confirm'),
				cancelButtonText: t('common.cancel'),
				type: 'warning',
				beforeClose: async (action, instance, done) => {
					if (action === 'confirm') {
						instance.confirmButtonLoading = true
						try {
							await regSuccess()
							done()
						} catch (error) {
							instance.confirmButtonLoading = false
						}
						return
					}
					done()
				},
			})
			return
		}
		regSuccess()
	})
}

const regSuccess = async () => {
	try {
		const { data } = await register(
			{
				...login_form.value,
				brand_id: state.platForm.id,
				invite_code: !needCode.value ? invite_code : undefined,
			},
			loading
		)
		Cookies.set('watch_live_status', 1, { expires: 30, domain: getNowDomain() }) //后端需要此参数
		ElMessage.success(t('login.reg_success'))
		window.location.href = window.location.origin + '/?token=' + data
	} catch (error: any) {
		if (error.data?.code === 1121) needCode.value = true
	}
}

const plainfo = computed(() => {
	return state.platForm
})

const initLoading = ref(false)
const domainLimit = ref<any>({})
const initConfig = async () => {
	const { data } = await getLimit(initLoading)
	domainLimit.value = data
}

const categorys = ref<any[]>([])
const initCategorys = async () => {
	if (!plainfo.value.category_status) return
	const { data } = await getCategorys({ brand_id: plainfo.value.id }, initLoading)
	categorys.value = data
}

const validCategory = (_: any, val: any, cb: any) => {
	if (plainfo.value.category_require && !val) cb(t('login.pl_category'))
	cb()
}

const logoType = inject<Ref<string>>('logoType', ref('login'))
watch(
	logoType,
	() => {
		if (logoType.value === 'register') {
			initConfig()
			initCategorys()
		}
	},
	{ immediate: true }
)
</script>

<style scoped lang="scss">
.register {
	h2 {
		font-size: 18px;
	}

	:deep(.el-form-item) {
		margin-bottom: 22px;
	}

	.form {
		margin-top: 20px;

		.form-btn {
			margin-top: 20px;

			button {
				width: 100%;
			}

			p {
				margin-top: 20px;

				span {
					color: var(--color-primary);
					cursor: pointer;
				}
			}
		}

		.clause {
			text-align: left;

			a {
				color: var(--color-primary);
			}
		}
	}
}
</style>
